/**
 * Saved-query hooks and per-type table chrome (ADR-038 Phase H).
 *
 * Two kinds of state, deliberately stored in two different places:
 *
 * - **Saved queries** (`useSavedQueries` and friends) are *documents* —
 *   named questions an operator re-opens. They live in
 *   ``auth.saved_queries`` behind ``/api/query/saved``, because if they
 *   lived in the Profile, applying a Profile would replace the operator's
 *   whole query library (D17).
 * - **Table chrome** (:func:`useQueryChrome`) — column visibility, order,
 *   widths, page size — *is* per-user working state, so it lives in the
 *   ADR-033 Profile document's ``query`` section and rides Profile
 *   apply/save like the theme and the workspace (D13 / REQ-UI-128).
 *
 * The chrome hook resolves stored keys against the **live catalog**: a
 * column the type no longer has is dropped, and a column it has gained is
 * appended. A Profile saved against an older catalog therefore neither
 * hides a new column nor resurrects a removed one.
 *
 * Cross-references:
 *   - REQ-UI-125 (save / re-open / delete), REQ-UI-126 (relative window),
 *     REQ-UI-128 (chrome persists and is restored by Profile apply).
 *   - REQ-QUERY-048; feature spec §2.8; ADR-038 D13 / D17 / D18.
 */
import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { ApiError, apiFetch } from "@/lib/api-client";
import { usePreferences } from "@/preferences/preferences-context";
import type { components } from "@/lib/api-types.gen";
import {
  type DataTableColumn,
  type SortState,
  defaultVisibleColumnKeys,
  resolveColumnOrder,
} from "@/data-table/data-table-model";
import {
  DEFAULT_TIME_RANGE,
  type QueryRow,
  type QueryableType,
  type TimeRangeSelection,
  type WirePredicate,
} from "@/query/query-model";

/** One saved query on the wire. */
export type SavedQuery = components["schemas"]["SavedQuery"];
/** The stored query document: window, predicate, sort, projection. */
export type SavedQueryDocument = components["schemas"]["SavedQueryDocument"];
/** Per-data-type table chrome as stored in the Profile document. */
export type QueryTableChrome = components["schemas"]["QueryTableChrome"];

/**
 * The ``query`` section version the BFF treats as current. Every write of the
 * section must carry it (D13 / REQ-UI-128).
 *
 * A stale stamp loses data rather than being cosmetic: the BFF migrates an
 * older section on save exactly as it migrates legacy data. That is how the
 * ``notifications`` section lost every explicit "All" (REQ-UI-130; see
 * ``NOTIFICATION_SCHEMA_VERSION``). This constant is what keeps a future
 * migration of this section from rewriting fresh writes.
 *
 * ``tsc`` cannot hold it to the BFF, because the generated type is ``number``.
 * ``preferences/profile-section-versions.test.ts`` pins it to the ``@default``
 * in ``api-types.gen.ts`` and requires every writer of the section to stamp it.
 */
export const QUERY_SCHEMA_VERSION: components["schemas"]["QuerySettings"]["query_schema_version"] = 1;

const SAVED_QUERIES_KEY = ["query", "saved"] as const;

/** Fetch the caller's saved queries. */
export function useSavedQueries(): UseQueryResult<SavedQuery[], ApiError> {
  return useQuery<SavedQuery[], ApiError>({
    queryKey: SAVED_QUERIES_KEY,
    queryFn: async ({ signal }) => {
      const raw = await apiFetch<unknown>("/api/query/saved", { signal });
      return Array.isArray(raw) ? (raw as SavedQuery[]) : [];
    },
    retry: (failureCount, error) =>
      error instanceof ApiError && error.status >= 400 && error.status < 500
        ? false
        : failureCount < 1,
  });
}

/** What the picker writes when saving or renaming a query. */
export interface SavedQueryWrite {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly typeId: string;
  readonly document: SavedQueryDocument;
}

/** Create or replace a saved query, then refresh the library. */
export function useSaveQuery(): {
  save: (write: SavedQueryWrite, options: { replace: boolean }) => Promise<SavedQuery>;
  isSaving: boolean;
  error: ApiError | null;
} {
  const client = useQueryClient();
  const mutation = useMutation<SavedQuery, ApiError, { write: SavedQueryWrite; replace: boolean }>({
    mutationFn: async ({ write, replace }) => {
      const body = {
        name: write.name,
        description: write.description,
        type_id: write.typeId,
        document: write.document,
      };
      if (replace) {
        return apiFetch<SavedQuery>(`/api/query/saved/${encodeURIComponent(write.id)}`, {
          method: "PUT",
          body: JSON.stringify(body),
        });
      }
      // The id is caller-supplied so a retried create is idempotent rather
      // than duplicating the query (CLAUDE.md §10).
      return apiFetch<SavedQuery>("/api/query/saved", {
        method: "POST",
        body: JSON.stringify({ id: write.id, ...body }),
      });
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: SAVED_QUERIES_KEY });
    },
  });

  return {
    save: (write, options) => mutation.mutateAsync({ write, replace: options.replace }),
    isSaving: mutation.isPending,
    error: mutation.error,
  };
}

/** Delete a saved query, then refresh the library. */
export function useDeleteSavedQuery(): {
  remove: (id: string) => Promise<void>;
  isDeleting: boolean;
} {
  const client = useQueryClient();
  const mutation = useMutation<void, ApiError, string>({
    mutationFn: async (id) => {
      await apiFetch<void>(`/api/query/saved/${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: SAVED_QUERIES_KEY });
    },
  });
  return {
    remove: (id) => mutation.mutateAsync(id),
    isDeleting: mutation.isPending,
  };
}

/**
 * Read a saved document's time window back into the editable selection.
 *
 * A stored relative window is the default and the point of D18 — it
 * re-runs against a live window rather than the instant it was saved. An
 * unrecognised or malformed stored window falls back to the 24-hour
 * default rather than throwing: a corrupt saved query should open with a
 * sensible window and let the operator fix it, not refuse to open.
 */
export function timeRangeFromDocument(document: SavedQueryDocument): TimeRangeSelection {
  const stored = document.time_range as Record<string, unknown> | undefined;
  if (stored?.mode === "absolute") {
    const start = typeof stored.start === "string" ? stored.start : "";
    const end = typeof stored.end === "string" ? stored.end : "";
    return start === "" || end === "" ? DEFAULT_TIME_RANGE : { mode: "absolute", start, end };
  }
  const seconds = stored?.duration_seconds;
  return typeof seconds === "number" && seconds > 0
    ? { mode: "relative", durationSeconds: seconds }
    : DEFAULT_TIME_RANGE;
}

/** Read a saved document's sort back into the table's sort state. */
export function sortFromDocument(document: SavedQueryDocument): SortState {
  const stored = document.sort;
  if (!Array.isArray(stored)) return [];
  return stored
    .map((rule) => rule as Record<string, unknown>)
    .filter((rule) => typeof rule.attribute === "string")
    .map((rule) => ({
      key: rule.attribute as string,
      direction: rule.direction === "asc" ? ("asc" as const) : ("desc" as const),
    }));
}

/**
 * The saved predicate, or ``null`` when the query had none.
 *
 * The wire type is an open map — the BFF stores the tree unparsed and
 * re-validates it on execute — so this is an unchecked widening by design.
 * The predicate is checked where it matters: the BFF re-parses it against
 * the caller's role-filtered type on every run, and the panel re-validates
 * it against the catalog before it can be edited or re-saved.
 */
export function predicateFromDocument(document: SavedQueryDocument): WirePredicate | null {
  const stored = document.predicate;
  return stored === null || stored === undefined ? null : (stored as unknown as WirePredicate);
}

/** The resolved per-type table chrome plus its writers. */
export interface QueryChrome {
  readonly visibleColumns: ReadonlySet<string>;
  readonly columnOrder: readonly string[];
  readonly columnSizing: Readonly<Record<string, number>>;
  readonly pageSize: number | null;
  readonly setVisibleColumns: (next: ReadonlySet<string>) => void;
  readonly setColumnOrder: (next: readonly string[]) => void;
  readonly setColumnSizing: (next: Record<string, number>) => void;
  readonly setPageSize: (next: number) => void;
}

/**
 * Per-data-type table chrome, read from and written to the Profile
 * document's ``query`` section (REQ-UI-128 / D13).
 *
 * Every setter merges into the *whole* ``query.by_type`` map before
 * writing, so persisting one type's chrome never drops another's — the
 * same round-trip-the-rest discipline the list-sort menu follows.
 *
 * Every write stamps :data:`QUERY_SCHEMA_VERSION`, not the version it
 * read. The section is rebuilt from this build's types, so the stamp must
 * name this build's version; the BFF migrates by what the stamp claims.
 * A section read from the BFF is already current (GET validates), so the
 * two only differ for a working state that bypassed the BFF — and there,
 * carrying the read version forward is the defect.
 */
export function useQueryChrome(
  type: QueryableType | null,
  columns: readonly DataTableColumn<QueryRow>[],
): QueryChrome {
  const { workingState, setWorkingState } = usePreferences();
  const typeId = type?.type_id ?? null;
  const section = workingState.query;
  const stored: QueryTableChrome | undefined =
    typeId === null ? undefined : section?.by_type?.[typeId];

  const persist = useCallback(
    (patch: Partial<QueryTableChrome>): void => {
      if (typeId === null) return;
      const existing = section?.by_type?.[typeId] ?? {};
      void setWorkingState({
        query: {
          query_schema_version: QUERY_SCHEMA_VERSION,
          by_type: {
            ...(section?.by_type ?? {}),
            [typeId]: { ...existing, ...patch },
          },
        },
      });
    },
    [section, setWorkingState, typeId],
  );

  // Resolve stored keys against the LIVE catalog: a column the type no
  // longer has is dropped, one it has gained is appended. A Profile saved
  // against an older catalog must neither hide a new column nor resurrect
  // a removed one.
  const known = useMemo(() => new Set(columns.map((column) => column.key)), [columns]);

  const visibleColumns = useMemo<ReadonlySet<string>>(() => {
    const persisted = (stored?.visible_columns ?? []).filter((key) => known.has(key));
    return persisted.length > 0 ? new Set(persisted) : defaultVisibleColumnKeys(columns);
  }, [stored?.visible_columns, known, columns]);

  const columnOrder = useMemo(
    () => resolveColumnOrder(columns, stored?.column_order),
    [columns, stored?.column_order],
  );

  const columnSizing = useMemo<Record<string, number>>(() => {
    const sizing: Record<string, number> = {};
    for (const [key, width] of Object.entries(stored?.column_widths ?? {})) {
      if (known.has(key) && typeof width === "number") sizing[key] = width;
    }
    return sizing;
  }, [stored?.column_widths, known]);

  return {
    visibleColumns,
    columnOrder,
    columnSizing,
    pageSize: stored?.page_size ?? null,
    setVisibleColumns: (next) => persist({ visible_columns: [...next] }),
    setColumnOrder: (next) => persist({ column_order: [...next] }),
    setColumnSizing: (next) => persist({ column_widths: next }),
    setPageSize: (next) => persist({ page_size: next }),
  };
}
